import React, { useRef, useEffect } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface NewsArticle {
  id: string;
  title: string;
  subtitle?: string;
}

interface NewsTickerProps {
  articles: NewsArticle[];
}

const NewsTicker: React.FC<NewsTickerProps> = ({ articles }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Show top 5 on mobile, top 15 on desktop
  const displayCount = isMobile ? 5 : 15;
  const displayArticles = articles.slice(0, displayCount);

  // Flower symbols for round robin
  const flowers = ['✿', '❀', '❁', '✾', '✽', '❃', '❊', '❋'];
  
  // Rainbow colors
  const rainbowColors = [
    '#FF6B6B', // Red
    '#FF8C42', // Orange
    '#FFD93D', // Yellow
    '#6BCF7F', // Green
    '#4D96FF', // Blue
    '#9D84FF', // Indigo
    '#C77DFF', // Violet
    '#FF6B9D', // Pink (to complete the cycle)
  ];

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || displayArticles.length === 0) return;

    // Wait for layout to calculate widths
    const timer = setTimeout(() => {
      const firstChild = container.firstElementChild as HTMLElement;
      if (firstChild) {
        const contentWidth = firstChild.offsetWidth;
        
        // Set animation duration based on content width (adjust speed as needed)
        // 40 pixels per second - adjust this value to change speed
        const duration = Math.max(20, contentWidth / 40);
        
        container.style.setProperty('--scroll-width', `${contentWidth}px`);
        container.style.setProperty('--scroll-duration', `${duration}s`);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [displayArticles]);

  if (displayArticles.length === 0) {
    return null;
  }

  const handleArticleClick = (articleId: string) => {
    navigate(`/news/${articleId}`);
  };

  const renderTickerItems = () => {
    return displayArticles.map((article, index) => {
      // Cycle through flowers and colors in round robin
      // Bullet before each item (for first item, use last index to wrap around)
      const bulletIndex = (index - 1 + displayArticles.length) % displayArticles.length;
      const bulletFlowerIndex = bulletIndex % flowers.length;
      const bulletColorIndex = bulletIndex % rainbowColors.length;
      const bulletFlower = flowers[bulletFlowerIndex];
      const bulletColor = rainbowColors[bulletColorIndex];

      return (
        <React.Fragment key={`${article.id}-${index}`}>
          {/* Bullet before each item */}
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "0.9rem", md: "1.05rem" },
              color: bulletColor,
              userSelect: "none",
              flexShrink: 0,
              mx: { xs: 1.5, md: 2 },
            }}
          >
            {bulletFlower}
          </Typography>
          <Typography
            component="span"
            onClick={() => handleArticleClick(article.id)}
            sx={{
              fontSize: { xs: "0.8125rem", md: "0.875rem" },
              color: "text.primary",
              cursor: "pointer",
              fontWeight: 500,
              transition: "color 0.2s",
              "&:hover": {
                color: "primary.main",
                textDecoration: "underline",
              },
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {article.title}
          </Typography>
        </React.Fragment>
      );
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        px: 1,
        py: 0.5,
        overflow: "hidden",
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        my: 0,
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          width: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            whiteSpace: "nowrap",
            width: "max-content",
            "@keyframes scroll": {
              "0%": {
                transform: "translateX(0)",
              },
              "100%": {
                transform: "translateX(calc(-1 * var(--scroll-width)))",
              },
            },
            animation: "scroll var(--scroll-duration, 30s) linear infinite",
          }}
        >
          {/* First set of items */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {renderTickerItems()}
          </Box>
          {/* Duplicate set for seamless loop */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {renderTickerItems()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewsTicker;

